import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { getOrganizationInitials } from '../../helpers/organization.helpers';
import {
  loadOrganizationLogoSrc,
  revokeOrganizationLogoSrc,
} from '../../helpers/organization-logo.helper';

interface OrgLogoProps {
  name: string;
  logoUrl: string | null;
  size?: number;
  onBroken?: () => void;
}

const OrgLogo: React.FC<OrgLogoProps> = ({
  name,
  logoUrl,
  size = 40,
  onBroken,
}) => {
  const theme = useTheme();
  const [displaySrc, setDisplaySrc] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);
  const blobRef = useRef<string | null>(null);
  const initials = getOrganizationInitials(name);

  useEffect(() => {
    setBroken(false);
    const trimmed = logoUrl?.trim();
    if (!trimmed) {
      setDisplaySrc(null);
      return;
    }

    let cancelled = false;

    loadOrganizationLogoSrc(trimmed).then((src) => {
      if (cancelled) {
        revokeOrganizationLogoSrc(src);
        return;
      }
      if (!src) {
        setBroken(true);
        setDisplaySrc(null);
        onBroken?.();
        return;
      }
      revokeOrganizationLogoSrc(blobRef.current);
      blobRef.current = src.startsWith('blob:') ? src : null;
      setDisplaySrc(src);
    });

    return () => {
      cancelled = true;
      revokeOrganizationLogoSrc(blobRef.current);
      blobRef.current = null;
    };
  }, [logoUrl]);

  if (!displaySrc || broken) {
    return (
      <Box
        aria-label={`${name} avatar`}
        sx={{
          width: size,
          height: size,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          color: theme.palette.primary.main,
          fontWeight: 700,
          fontSize: size * 0.38,
          flexShrink: 0,
        }}
      >
        {initials}
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={displaySrc}
      alt={`${name} logo`}
      aria-label={`${name} logo`}
      onError={() => {
        setBroken(true);
        setDisplaySrc(null);
        onBroken?.();
      }}
      sx={{
        width: size,
        height: size,
        objectFit: 'contain',
        borderRadius: 1.5,
        flexShrink: 0,
        bgcolor: alpha(theme.palette.text.primary, 0.04),
        p: 0.25,
      }}
    />
  );
};

export default OrgLogo;
